import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import BookmarkedCompanySchema from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import BookmarkedIndividualSchema from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import BookmarkedTeamSchema from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import { z } from "zod";

export type BookmarksResponseJSON = z.infer<typeof BookmarksResponseSchema.BookmarksResponseJSONZ>;
export type BookmarksResponseParams = z.infer<typeof BookmarksResponseSchema.BookmarksResponseZ>;

export default class BookmarksResponseSchema {
  public static readonly BookmarksResponseJSONZ = z.object({
    company: z.array(BookmarkedCompanySchema.BookmarkedCompanyJSONZ),
    individual: z.array(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ),
    team: z.array(BookmarkedTeamSchema.BookmarkedTeamJSONZ),
  });
  public static readonly BookmarksResponseZ = z.object({
    company: z.array(BookmarkedCompanySchema.BookmarkedCompanyZ),
    individual: z.array(BookmarkedIndividualSchema.BookmarkedIndividualZ),
    team: z.array(BookmarkedTeamSchema.BookmarkedTeamZ),
  });

  public static validate(values: unknown): BookmarksResponseParams {
    return parseOrThrow(
      BookmarksResponseSchema.BookmarksResponseZ, 
      values, 
      { 
        logError: true,
        schemaName: "BookmarksResponseSchema",
        operation: "validate",
        payloadSample: values
      }
    );
  }

  public static validateJSON(values: unknown): BookmarksResponseJSON {
    return parseOrThrow(
      BookmarksResponseSchema.BookmarksResponseJSONZ, 
      values, 
      { 
        logError: true,
        schemaName: "BookmarksResponseSchema",
        operation: "validateJSON",
        payloadSample: values 
      }
    );
  }

  public static isValid(values: unknown): values is BookmarksResponseParams {
    try {
      BookmarksResponseSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is BookmarksResponseJSON {
    try {
      BookmarksResponseSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: BookmarksResponseJSON): BookmarksResponseParams {
    const parsed = parseOrThrow(
      BookmarksResponseSchema.BookmarksResponseJSONZ, 
      data, 
      { 
        logError: true,
        schemaName: "BookmarksResponseSchema",
        operation: "deserialize",
        payloadSample: data
      }
    );
    return BookmarksResponseSchema.codec.decode(parsed);
  }

  public static serialize(params: BookmarksResponseParams): BookmarksResponseJSON {
    const parsed = BookmarksResponseSchema.validate(params);
    return BookmarksResponseSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(BookmarksResponseSchema.BookmarksResponseJSONZ, BookmarksResponseSchema.BookmarksResponseZ, {
    decode: (data) => ({
      company: BookmarkedCompanySchema.deserializeArray(data.company),
      individual: BookmarkedIndividualSchema.deserializeArray(data.individual),
      team: BookmarkedTeamSchema.deserializeArray(data.team),
    }),
    encode: (bookmarks) => ({
      company: BookmarkedCompanySchema.serializeArray(bookmarks.company),
      individual: BookmarkedIndividualSchema.serializeArray(bookmarks.individual),
      team: BookmarkedTeamSchema.serializeArray(bookmarks.team),
    }),
  });
}
